import { Component, OnInit } from '@angular/core';
import { DialogBase } from '@routes/dialogs/dialog.base';
import { finalize } from 'rxjs/operators';
import Utils from '@shared/utils/utils';
import { FormGroup, Validators } from '@angular/forms';
import { Constants } from '@shared/constants/constants';
import Role from '@shared/constants/role';
import { Company } from '@shared/enums/company';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.scss']
})
export class CustomerDetailComponent extends DialogBase implements OnInit {
  title: any;
  customerType: any;
  dealerStore: any;

  bpNo: any;
  dealerBpNo: any;
  contactId: any;
  employeeNo: any;
  customerData!: any;
  storeCode: any;
  role: string;
  optOut: boolean;
  isStoreContact: boolean = false;
  email: string;

  detailForm: FormGroup;
  country;
  company;
  companies = Company;
  isDealerStaff = false;
  isDealerManager = false;
  isEditable = false;
  isFromStoreContact = false;

  isCreate = true;

  // address parts that identify a service location
  private static readonly addressKeys = ['address1', 'address2', 'city', 'state', 'zipcode', 'country'];

  languages = [
    { code: 'EN', name: 'English' },
    { code: 'FR', name: 'French' },
  ]

  get form(): any {
    return this.detailForm.controls;
  }

  ngOnInit(): void {
    console.log(`employeeNo = ${this.employeeNo}, bpNo = ${this.bpNo}`);
    this.company = this.authService.getCurrentCompany();
    this.country = this.authService.getCurrentCountry(this.company);
    this.isDealerStaff = this.authService.getRolesByCompany(this.company).includes(Role.seaImDealerStaff);
    this.isDealerManager = this.authService.getRolesByCompany(this.company).includes(Role.seaImDealerSuperVisor);
    this.isEditable = [Company.Sea, Company.Seca].includes(this.company);
    this.setFormControl();

    if (this.customerType === Constants.customerType.STORE) {
      if (this.contactId && this.bpNo) {
        this.isCreate = false;
        // For store contacts, fetch BP details to get address
        if (this.isStoreContact) {
          // this.getBpDetailForStoreContact();
          if(this.employeeNo) {
            this.getBpDetail(this.employeeNo);
          } else {
            this.getContactDetail();
          }
        } else {
          this.getContactDetail();
        }
      }
    } else {
      if (this.bpNo) {
        this.isCreate = false;
        this.getBpDetail();
      }
    }
    this.detailForm.controls['employeeNo'].disable();
  }

  setFormControl() {
    this.detailForm = this.fb.group({
      bpNo: null,
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      email: [this.companies.Seca].includes(this.company) ? null : [null, Validators.compose([Validators.required, Validators.email])],
      cellPhone: null,
      homePhone: [null, Validators.required],
      workPhone: null,
      language: null,
      employeeNo: null,
      optOut: null,
    });
  }

  getBpDetail(bpNo?) {
    this.spinnerService.start();

    this.apiService.bp.getBpDetail(bpNo || this.bpNo)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.customerData = res;
        this.customerData.employeeNo = this.employeeNo;
        this.customerData.email = this.email;
        this.detailForm.controls['email'].disable();
        this.customerData.optOut = this.optOut;
        this.setData(this.customerData);
      }, err => Utils.showError(err));
  }

  getContactDetail() {
    this.spinnerService.start();
    const bpNo = this.dealerBpNo ? this.dealerBpNo : this.bpNo;
    this.apiService.dealer.getDealerContactForTicket(bpNo, this.contactId)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.customerData = res;
        this.detailForm.controls['email'].disable();
        this.setData(this.customerData);
      }, err => Utils.showError(err));
  }

  setData(customerData) {
    Object.keys(this.form).forEach(key => {
      console.log(`key = ${key}, value = ${customerData[key]}`);
      this.form[key].setValue(customerData[key]);
    });
    if (!this.form['homePhone'].value) {
      this.form['homePhone'].setValue(customerData['cellPhone'] || customerData['workPhone']);
      // this.form['homePhone'].setValue(customerData['workPhone']);
    }
  }

  // the address row is only shown for customers and store contacts
  get hasAddressInput(): boolean {
    return this.customerType === Constants.customerType.CUSTOMER || this.isStoreContact;
  }

  private static normalize(value): string {
    return `${value || ''}`.trim().toUpperCase();
  }

  // true when the typed address differs from the one loaded for this customer or contact
  private addressChanged(address): boolean {
    return CustomerDetailComponent.addressKeys
      .some(key => CustomerDetailComponent.normalize(address?.[key])
        !== CustomerDetailComponent.normalize(this.customerData?.address?.[key]));
  }

  // 'customer' and 'contact' are separate records in GCIC and the warning has to name the right one
  private get recordLabel(): string {
    return this.isStoreContact ? 'contact' : 'customer';
  }

  // true when saving rewrites a record that already exists in GCIC instead of creating one;
  // only then can a changed address reach service requests created earlier
  private get updatesExistingRecord(): boolean {
    return this.isStoreContact ? !!this.employeeNo : !!this.bpNo;
  }

  // Asks how far a changed address should reach.
  // Resolves true to also rewrite the customer or contact record in GCIC, false to keep the change
  // on this service request, null when the user cancels.
  private async askAddressScope(lead: string): Promise<boolean | null> {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Address Changed',
      html: `${lead}<br/><br/>`
        + `Updating the ${this.recordLabel} record replaces the address on <b>every service request of `
        + `that ${this.recordLabel}, including the ones already created</b>. `
        + `Keeping it on this request leaves the ${this.recordLabel} record as it is.`,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'This request only',
      denyButtonText: `Update ${this.recordLabel} record`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      denyButtonColor: '#d33',
    });
    if (result.isConfirmed) {
      return false;
    }
    if (result.isDenied) {
      return true;
    }
    return null;
  }

  // Looks for a customer that GCIC would match on the typed phone number or e-mail.
  // GCIC reuses that BP instead of creating a new one, which is how a new request ends up
  // rewriting the address of earlier requests.
  private async findExistingCustomer(customerData): Promise<any> {
    const searches = [customerData.homePhone, customerData.email].filter(x => !!x);
    for (const search of searches) {
      try {
        const res = await this.apiService.bp.getBp(search).toPromise();
        const match = (res?.customers || []).find(x => !!x?.bpNo);
        if (match) {
          return match;
        }
      } catch (e) {
        // a failed lookup must not block the save; fall through to the normal flow
        console.error('findExistingCustomer:', e);
      }
    }
    return null;
  }

  async getContactBpNumber(contactData): Promise<string> {
    return new Promise(async (resolve) => {
      let bpNo = '';
      if ([this.companies.Sea, this.companies.SeaIm].includes(this.company)) {
        try {
          bpNo = await this.bpService.saveContactBp(contactData, [], this.company);
        } catch (error) {
          await this.spinnerService.stop()
          await Swal.fire({
            title: error.error.message,
            text: error.error.payload.errorFields[0].message,
            icon: 'error',
          });
          return;
        }
      }
      return resolve(bpNo);
    })
  }


  // keepCustomerRecord: carry the typed values into this service request without writing the
  // customer record in GCIC, so the address of requests created earlier stays untouched
  async save(fromConfirm = false, keepCustomerRecord = false, existingCustomer?: any) {
    let customerData = this.detailForm.getRawValue();
    // Apply address value redefinition for customers and store contacts
    if (this.customerType === Constants.customerType.CUSTOMER || this.isStoreContact) {
      customerData = this.valueRedefinition(customerData);
    }
    customerData.workPhone = customerData.homePhone;
    customerData.cellPhone = customerData.homePhone;
    customerData.language = customerData.language || 'EN';
    if (!this.bpNo) {
      delete customerData.bpNo;
    }

    // Validate address for customers and store contacts
    if (this.detailForm.invalid ||
        ((this.customerType === Constants.customerType.CUSTOMER || this.isStoreContact)
         && this.googlePlacesAutocomplete.validAddress(customerData.address))) {
      this.googlePlacesAutocomplete.isAddressValid$.next(true);
      this.detailForm.markAllAsTouched();
      return;
    }
    this.googlePlacesAutocomplete.isAddressValid$.next(false);

    // GCIC matches an existing customer on phone number or e-mail rather than creating a new one,
    // so a "new customer" can silently replace the address on that customer's earlier requests.
    if (this.customerType === Constants.customerType.CUSTOMER
      && !customerData.bpNo && !keepCustomerRecord && !existingCustomer) {
      this.spinnerService.start();
      const existing = await this.findExistingCustomer(customerData);
      this.spinnerService.stop();
      if (existing) {
        const applyToCustomerRecord = await this.askAddressScope(
          `This phone number or e-mail already belongs to an existing customer (BP ${existing.bpNo}).`);
        if (applyToCustomerRecord === null) {
          return;
        }
        await this.save(fromConfirm, !applyToCustomerRecord, existing);
        return;
      }
    }

    this.apiService.user.getUser(customerData.email).subscribe(
      res => {
        res.roles.map(role => {
          if (role.groups) {
            this.role = role.groups.join(', ');
          }
        });
      }, err => {
      }
    );

    if (keepCustomerRecord && this.customerType === Constants.customerType.CUSTOMER) {
      // hand the request the typed values on top of the customer GCIC would have matched
      const base = existingCustomer || this.customerData || {};
      this.onClose.next({ ...base, ...customerData, bpNo: customerData.bpNo || base.bpNo });
      this.bsModalRef.hide();
      return;
    }

    this.spinnerService.start();
    if (this.customerType === Constants.customerType.CUSTOMER) {
      this.apiService.bp.saveCustomer(customerData)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(res => {
          if ([this.companies.Seca, this.companies.SeaIm].includes(this.company) && !fromConfirm) {
            const message = customerData.bpNo ? 'Customer information saved successfully.<br/>Please click [Confirm] to apply Customer Information' :
              'Customer information saved successfully.';
            Swal.fire({
              icon: 'success',
              title: message,
              showConfirmButton: true,
            }).then((result) => {
              if (customerData.bpNo) {
                this.customerData = res;
              } else {
                this.onClose.next(res);
                this.bsModalRef.hide();
              }
            });
          } else {
            this.onClose.next(res);
            this.bsModalRef.hide();
          }
        }, err => Utils.showError(err));
    } else {
      let dealerContact;
      const useBpNo = this.bpNo || this.dealerBpNo;
      let contactBpNo = '';

      // Create BP for store contact only
      if (this.isStoreContact) {
        if (!customerData.tel) {
          customerData.tel = customerData.homePhone;
        }
        customerData.bpNo = useBpNo;
        if (keepCustomerRecord) {
          // skip the BP change so the contact keeps its address in GCIC
          contactBpNo = customerData.employeeNo || this.employeeNo || '';
        } else {
          contactBpNo = await this.getContactBpNumber(customerData);
        }
      }

      if ([Company.Sea].includes(this.company)) {
        await this.apiService.dealer.getDealer(useBpNo).subscribe(
          res => {
            this.storeCode = res._id;
          }
        )
        dealerContact = {
          firstName: customerData.firstName,
          lastName: customerData.lastName,
          tel: customerData.homePhone,
          email: customerData.email,
          ticket: true,
          contactId: this.contactId,
          employeeNo: this.isStoreContact ? contactBpNo : (this.customerData?.employeeNo || useBpNo),
          stores: [this.storeCode],
          role: this.role,
          optOut: customerData?.optOut,
          address: this.isStoreContact ? customerData.address : undefined
        }
      } else {
        dealerContact = {
          firstName: customerData.firstName,
          lastName: customerData.lastName,
          tel: customerData.homePhone,
          email: customerData.email,
          ticket: true,
          contactId: this.contactId,
          employeeNo: this.isStoreContact ? (contactBpNo || useBpNo) : (this.customerData?.employeeNo || this.dealerBpNo),
          role: this.role,
          optOut: customerData?.optOut,
          address: this.isStoreContact ? customerData.address : undefined
        }
      }

      this.apiService.dealer.saveContact(useBpNo, dealerContact)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(res => {
          let contact;
          if (res) {
            contact = res.find(r => r.email === customerData.email);
            // For store contacts, ensure the address and BP are included
            if (contact && this.isStoreContact) {
              if (customerData.address) {
                contact.address = customerData.address;
              }
              if (contactBpNo) {
                contact.employeeNo = contactBpNo;
                contact.bpNo = contactBpNo;
              }
            }
          }
          this.onClose.next(contact);
          this.bsModalRef.hide();
        }, err => Utils.showError(err));
    }
  }

  async confirm() {
    try {
      const customerData = this.hasAddressInput
        ? this.valueRedefinition(this.detailForm.getRawValue())
        : this.detailForm.getRawValue();

      // this customer or contact already exists, so a changed address would rewrite its record
      if (this.hasAddressInput && this.updatesExistingRecord && this.addressChanged(customerData.address)) {
        const recordBpNo = this.isStoreContact ? (this.employeeNo || this.bpNo) : this.bpNo;
        const applyToCustomerRecord = await this.askAddressScope(
          `You changed the address of an existing ${this.recordLabel}${recordBpNo ? ` (BP ${recordBpNo})` : ''}.`);
        if (applyToCustomerRecord === null) {
          return;
        }
        await this.save(true, !applyToCustomerRecord);
        return;
      }
      await this.save(true);
    } catch (error) {
    }
  }

  newContact() {
    // Reset form to create new contact
    this.isCreate = true;
    this.detailForm.reset();
    this.customerData = null;
    this.bpNo = null;
    this.contactId = null;
    this.detailForm.controls['email'].enable();

    // Clear address fields for customers and store contacts
    if (this.customerType === Constants.customerType.CUSTOMER || this.isStoreContact) {
      const addressFields = ['edit_street_number', 'edit_route', 'edit_address2', 'edit_locality', 'edit_administrative_area_level_1', 'edit_postal_code', 'edit_country'];
      addressFields.forEach(fieldId => {
        const element = document.getElementById(fieldId) as HTMLInputElement;
        if (element) {
          element.value = '';
        }
      });

      // Also clear the autocomplete input field
      const autocompleteInput = document.getElementById('autocomplete_input') as HTMLInputElement;
      if (autocompleteInput) {
        autocompleteInput.value = '';
      }
    }
  }

  valueRedefinition(value) {
    value = {
      ...value,
      address: {
        address1: (document.getElementById('edit_route') as HTMLInputElement).value,
        address2: (document.getElementById('edit_address2') as HTMLInputElement).value,
        city: (document.getElementById('edit_locality') as HTMLInputElement).value,
        state: (document.getElementById('edit_administrative_area_level_1') as HTMLInputElement).value,
        zipcode: (document.getElementById('edit_postal_code') as HTMLInputElement).value,
        country: (document.getElementById('edit_country') as HTMLInputElement).value,
      }
    };

    return value;
  }
}
